<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const router = useRouter()
</script>

<template>
  <aside
    id="sidebar"
    class="w-64 bg-white border-r border-gray-200 h-screen sticky top-16"
  >
    <nav class="p-4 overflow-y-auto h-full pb-8">
      <ul class="space-y-1">
        <li>
          <span
            @click="router.push('/developer/dashboard')"
            class="flex items-center space-x-3 px-3 py-2 bg-primary/10 text-primary rounded-md cursor-pointer"
          >
            <i class="fas fa-tachometer-alt w-4"></i>
            <span class="font-medium">{{ $t('developer.sidebar.dashboard') }}</span>
          </span>
        </li>

        <!-- My Projects -->
        <li class="pt-3 pb-1">
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">My Projects</p>
        </li>
        <li>
          <span @click="router.push('/developer/projects')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-folder-open w-4"></i>
            <span>All Projects</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/credits')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-leaf w-4"></i>
            <span>Credit Portfolio</span>
          </span>
        </li>

        <!-- Project Workflow -->
        <li class="pt-3 pb-1">
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">Project Workflow</p>
        </li>
        <li>
          <span @click="router.push('/developer/project/submit-pcn')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-file-signature w-4"></i>
            <span>Submit PCN</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/project/loa-status')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-stamp w-4"></i>
            <span>LoA Status</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/project/submit-pin')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-lightbulb w-4"></i>
            <span>{{ $t('developer.sidebar.project_ideas') }}</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/project/submit-pdd')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-file-alt w-4"></i>
            <span>{{ $t('developer.sidebar.design_documents') }}</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/project/validation-status')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-clipboard-check w-4"></i>
            <span>Validation Status</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/project/registration-certificate')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-award w-4"></i>
            <span>Registration Certificate</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/upload-reports')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-chart-line w-4"></i>
            <span>{{ $t('developer.sidebar.monitoring_reports') }}</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/project/verification-status')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-search-plus w-4"></i>
            <span>Verification Status</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/request-credits')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-certificate w-4"></i>
            <span>{{ $t('developer.sidebar.credit_issuance') }}</span>
          </span>
        </li>

        <!-- Credit Actions -->
        <li class="pt-3 pb-1">
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">Credit Actions</p>
        </li>
        <li>
          <span @click="router.push('/developer/retire-credits')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-fire w-4"></i>
            <span>Retire Credits</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/transfer-credits')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-exchange-alt w-4"></i>
            <span>Transfer Credits</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/corresponding-adjustments')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-balance-scale w-4"></i>
            <span>Corresponding Adjustments</span>
          </span>
        </li>

        <!-- Compliance -->
        <li class="pt-3 pb-1">
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">Compliance</p>
        </li>
        <li>
          <span @click="router.push('/developer/grievances')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-comment-dots w-4"></i>
            <span>Grievances</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/legacy-registration')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-archive w-4"></i>
            <span>{{ $t('developer.sidebar.legacy_projects') }}</span>
          </span>
        </li>

        <!-- Account -->
        <li class="pt-3 pb-1">
          <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">Account</p>
        </li>
        <li>
          <span @click="router.push('/developer/profile')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-user-circle w-4"></i>
            <span>Profile & Settings</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/payments')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-receipt w-4"></i>
            <span>Payment History</span>
          </span>
        </li>
        <li>
          <span @click="router.push('/developer/notifications')" class="flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-md cursor-pointer">
            <i class="fas fa-bell w-4"></i>
            <span>Notifications</span>
          </span>
        </li>
      </ul>
    </nav>
  </aside>
</template>